import { useState } from "react";
import { useArsContext } from "../contexts";

export interface ArsBlockDropZoneProps {
  id: string;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  top: string;
  bottom: string;
}

export const ARSB_DROP_ZONE_HIGHLIGHT_COLOUR = "#4dabf7";

/**
 * Drop zone overlay for an ARS block, highlighting when a compatible item is dragged over it.
 *
 * @param props - Component props
 * @param props.id - ID for the drop zone element, used for test hooks
 * @param props.onDrop - Callback fired when a compatible item is dropped onto the zone
 * @param props.top - CSS value for the top position of the drop zone
 * @param props.bottom - CSS value for the bottom position of the drop zone
 * @returns A React element representing the drop zone overlay.
 */
export const ArsBlockDropZone = ({
  id,
  onDrop,
  top,
  bottom,
}: ArsBlockDropZoneProps): React.ReactElement => {
  const { state: arsState } = useArsContext();
  const [showHighlight, setShowHighlight] = useState<boolean>(false);

  return (
    <div
      id={id}
      className="ars-block-drop-zone"
      onDragOver={(e) => {
        e.preventDefault();
        // Can only drop when the current view is showing the right thing
        if (arsState.currentSidebar.view === "groupingFactor") {
          setShowHighlight(true);
          e.dataTransfer.dropEffect = "copy";
        } else {
          setShowHighlight(false);
          e.dataTransfer.dropEffect = "none";
        }
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "none";
        setShowHighlight(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setShowHighlight(false);
        onDrop(e);
      }}
      style={{
        backgroundColor: showHighlight ? ARSB_DROP_ZONE_HIGHLIGHT_COLOUR : undefined,
        border: showHighlight ? `2px solid ${ARSB_DROP_ZONE_HIGHLIGHT_COLOUR}` : undefined,
        bottom,
        left: 0,
        opacity: showHighlight ? "0.5" : "0",
        position: "absolute",
        right: 0,
        top,
        zIndex: 199,
      }}
    />
  );
};

ArsBlockDropZone.displayName = "ArsBlockDropZone";
